/**
 * Thin fetch wrapper for the BriefTaker FastAPI backend.
 *
 * Responsibilities:
 *  - Inject `Authorization: Bearer <token>` from an injected provider.
 *  - On a 401 response, attempt one token refresh and retry once.
 *  - Throw a typed `ApiError` for non-2xx responses.
 *
 * This module is framework-agnostic; the React layer wires it up via a small adapter
 * (see `useApiClient` consumers) so it stays trivially unit-testable.
 */

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns a fresh access token; called for every request and on 401 retry. */
  getToken: () => Promise<string>;
  /** Optional override (used by tests). */
  fetchImpl?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClient {
  request<T>(path: string, init?: RequestInit): Promise<T>;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  const base = opts.baseUrl.replace(/\/$/, "");

  async function call(path: string, init: RequestInit, token: string): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    // For FormData bodies (file upload) the browser must set `Content-Type`
    // itself so it can add the multipart boundary — never force JSON there.
    const isFormData =
      typeof FormData !== "undefined" && init.body instanceof FormData;
    if (init.body && !isFormData && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    headers.set("Accept", "application/json");
    return fetchImpl(`${base}${path}`, { ...init, headers });
  }

  return {
    async request<T>(path: string, init: RequestInit = {}): Promise<T> {
      let token = await opts.getToken();
      let response = await call(path, init, token);

      if (response.status === 401) {
        // Refresh once and retry.
        token = await opts.getToken();
        response = await call(path, init, token);
      }

      if (!response.ok) {
        let body: unknown = null;
        try {
          body = await response.json();
        } catch {
          body = await response.text().catch(() => null);
        }
        throw new ApiError(
          `API ${response.status} on ${path}`,
          response.status,
          body,
        );
      }

      if (response.status === 204) {
        return undefined as T;
      }
      return (await response.json()) as T;
    },
  };
}
